import moment from 'moment';
import { format } from 'date-fns';
import { isEmptyObject } from 'jquery';
import Cookies from 'universal-cookie';
import { NumericFormat } from 'react-number-format';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useContext, useEffect, useState } from 'react';
import { useForm, SubmitHandler, FieldValues, Controller } from 'react-hook-form';
import { Button, CircularProgress, Divider, Grid, TextField, Typography } from '@material-ui/core';

import { useStyles } from './styles';
import { AuthContext } from 'contexts/AuthContext';
import { supplierFormSchema } from './validations';
import { useEnvironment, useSnackbar } from 'hooks';
import { CNPJTextMaskCustom } from './formCustomMasks';
import { useAssignorGql } from './graphql/getAssignor';
import { useSupplierGql } from './graphql/getSupplier';
import { EditCell } from './components/editAssignorCell';
import { GetUserInfo } from 'components/util/Helpers/Auth';
import { useBankDetailsGql } from './graphql/getBankDetails';
import { DeleteCell } from './components/deleteAssignorCell';
import { PageBarHeader } from 'components/migrated/pageheader';
import { GenericTable } from 'components/migrated/generictable';
import { useUpdateSupplierGql } from './graphql/updateSupplier';
import { useSuppliersGql } from 'pages/invoices/graphql/suppliers';
import { useRegisterSupplierGql } from './graphql/registerSupplier';
import ListaVazia from 'components/util/listaVazia/.mixins/ListaVazia';
import { EditBankDetailsCell } from './components/editBankDetailsCell';
import { AssignorRegistration } from './components/assignorRegistration';
import { DeleteBankDetailsCell } from './components/deleteBankDetailsCell';
import { DatePicker } from 'components/migrated/hook-form-inputs/datepicker';
import { IOption } from 'components/migrated/enhancedautocomplete/interfaces';
import { BankDetailsRegistration } from './components/bankDetailsRegistration';
import { useSupplierRegister, SupplierContext } from 'contexts/complementaryRegistration';
import { EnhancedAutocomplete as Autocomplete } from 'components/migrated/enhancedautocomplete';

const companyTypeOptions = [
  { key: 1, value: 'COOPERATIVA' },
  { key: 2, value: 'EIRELI' },
  { key: 3, value: 'EPP' },
  { key: 4, value: 'LTDA' },
  { key: 5, value: 'ME' },
  { key: 6, value: 'MEI' },
  { key: 7, value: 'S/A ABERTA' },
  { key: 8, value: 'S/A FECHADA' }
];

const companySizeOptions = [
  { key: 1, value: 'MICRO' },
  { key: 2, value: 'PEQUENO' },
  { key: 3, value: 'MÉDIO' },
  { key: 4, value: 'GRANDE' }
];

const riskClassificationOptions = [
  { key: 'AA', value: 'Classificação de Risco AA' },
  { key: 'A', value: 'Classificação de Risco A' },
  { key: 'B', value: 'Classificação de Risco B' },
  { key: 'C', value: 'Classificação de Risco C' },
  { key: 'D', value: 'Classificação de Risco D' },
  { key: 'E', value: 'Classificação de Risco E' },
  { key: 'F', value: 'Classificação de Risco F' },
  { key: 'G', value: 'Classificação de Risco G' },
  { key: 'H', value: 'Classificação de Risco H' }
];

const receitaRegistrationStatusOptions = [
  { key: 'A', value: 'Ativa' },
  { key: 'U', value: 'Suspensa' },
  { key: 'I', value: 'Inapta' },
  { key: 'B', value: 'Baixada' },
  { key: 'L', value: 'Nula' },
  { key: 'F', value: 'Falência Decretada' }
];

export const ComplementaryRegistration = () => {
  const {
    user: { isVisaoTok }
  } = useContext(AuthContext);
  const { DEBUG } = useEnvironment();
  const { Snackbar, toggleSnackbar } = useSnackbar();
  const { supplierData, setSupplierData, resetSupplierData, hasChanges, setHasChanges } = useSupplierRegister();

  const [editMode, setEditMode] = useState(false);
  const [openAssignorModal, setOpenAssignorModal] = useState(false);
  const [openBankDetailsModal, setOpenBankDetailsModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<IOption | null>(null);

  const { runSuppliersGql, dataSuppliers, errorSuppliers, loadingSuppliers } = useSuppliersGql();
  const { runSupplierGql, dataSupplier, errorSupplier, loadingSupplier } = useSupplierGql();
  const { runRegisterSupplierMutation, errorRegisterSupplier, loadingRegisterSupplier } = useRegisterSupplierGql();
  const { runUpdateSupplierMutation, errorUpdateSupplier, loadingUpdateSupplier } = useUpdateSupplierGql();
  const { runAssignorGql, dataAssignor, errorAssignor, loadingAssignor } = useAssignorGql();
  const { runBankDetailsGql, dataBankDetails, errorBankDetails, loadingBankDetails } = useBankDetailsGql();

  const {
    control: controlSupplier,
    handleSubmit: handleSubmitSupplier,
    setValue: setValueSupplier,
    reset: resetSupplier,
    formState: formStateSupplier
  } = useForm<FieldValues>({
    defaultValues: supplierData,
    mode: 'onChange',
    resolver: yupResolver(supplierFormSchema)
  });

  const { dirtyFields: dirtyFieldsSupplier } = formStateSupplier;

  const cookies = new Cookies();
  const personIdLogged = cookies.get('User-Code');
  const color = cookies.get('System-Color');

  const classes = useStyles({ color });

  const refreshSupplierData = () => {
    if (selectedSupplier) {
      runSupplierGql({
        variables: {
          personId: +selectedSupplier?.key
        }
      });
    }
  };

  const handleOnSubmit: SubmitHandler<FieldValues> = async (values) => {
    if (!selectedSupplier) {
      return;
    }

    if (supplierData.assignors.length === 0 || supplierData.banksDetails.length === 0) {
      toggleSnackbar({
        message: 'Adicione uma pessoa cedente e uma conta bancária para continuar.',
        type: 'error'
      });
    }

    const formattedConstitutionDate = format(new Date(values.constitutionDate), 'dd/MM/yyyy');

    const supplier = {
      idFDEm: editMode ? supplierData.supplier.idFDEm : undefined,
      personId: +selectedSupplier?.key,
      personName: selectedSupplier?.value,
      cnpj: values.cnpj.replace(/\D/g, ''),
      riskClassification: values.riskClassification.key,
      annualBilling: +values.annualBilling,
      creditLimit: +values.creditLimit,
      socialCapital: +values.socialCapital,
      minApprovalSignatures: +values.minApprovalSignatures,
      constitutionDate: formattedConstitutionDate,
      companyType: +values.companyType.key,
      companySize: +values.companySize.key,
      receitaRegistrationStatus: values.receitaRegistrationStatus.key
    };

    const banksDetails = supplierData.banksDetails.map((bankDetails) => {
      return {
        ...bankDetails,
        item: {
          idFDBa: bankDetails.item.idFDBa,
          idFDEm: bankDetails.item.idFDEm,
          agency: bankDetails.item.agency,
          bankDataNotes: bankDetails.item.bankDataNotes,
          checkingAccount: bankDetails.item.checkingAccount,
          bankInstitutionId: +bankDetails.item.bank.key
        }
      };
    });

    const mutationVariables = {
      supplierRegister: {
        supplier,
        assignors: supplierData.assignors,
        banksDetails,
        personIdOperation: +personIdLogged
      }
    };

    const handleMutationSuccess = (message: string) => {
      toggleSnackbar({
        message,
        type: 'success'
      });
      refreshSupplierData();
    };

    try {
      if (!editMode) {
        await runRegisterSupplierMutation({ variables: mutationVariables });
        handleMutationSuccess('Cadastro criado com sucesso!');
      } else {
        await runUpdateSupplierMutation({ variables: mutationVariables });
        handleMutationSuccess('Cadastro atualizado com sucesso!');
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Erro ao cadastrar/atualizar fornecedor:', error);
    }
  };

  const columns: any = [
    {
      key: 'document',
      header: 'Documento',
      headerAlign: 'left',
      cellAlign: 'right',
      columnWidth: 200,
      customRender: (row: any) => {
        const label = row.cpf ?? row.cnpj;
        const style = row.strike ? { textDecoration: 'line-through' } : {};
        return <span style={style}>{label}</span>;
      }
    },
    {
      key: 'fullName',
      header: 'Nome Completo',
      headerAlign: 'left',
      cellAlign: 'left'
    },
    {
      key: 'edit',
      header: 'Editar',
      columnWidth: 100,
      customRender: (row: any) => <EditCell row={row} personIdLogged={personIdLogged} />
    },
    {
      key: 'delete',
      header: 'Excluir',
      columnWidth: 100,
      customRender: (row: any) => <DeleteCell row={row} personIdLogged={personIdLogged} />
    }
  ];

  const bankDetailsColumns: any = [
    {
      key: 'bank',
      header: 'Banco',
      headerAlign: 'left',
      cellAlign: 'left',
      columnWidth: 250,
      customRender: (row: any) => {
        const label = `${row.bank.key} - ${row.bank.value}`;
        const style = row.strike ? { textDecoration: 'line-through' } : {};
        return <span style={style}>{label}</span>;
      }
    },
    {
      key: 'agency',
      header: 'Agência',
      headerAlign: 'left',
      cellAlign: 'right',
      columnWidth: 100
    },
    {
      key: 'checkingAccount',
      header: 'Conta Corrente',
      headerAlign: 'left',
      cellAlign: 'right',
      columnWidth: 100
    },
    {
      key: 'bankDataNotes',
      header: 'Observações',
      headerAlign: 'left',
      cellAlign: 'left',
      limitTextSize: 40,
      columnWidth: 250
    },
    {
      key: 'edit',
      header: 'Editar',
      columnWidth: 100,
      customRender: (row: any) => <EditBankDetailsCell row={row} personIdLogged={personIdLogged} />
    },
    {
      key: 'delete',
      header: 'Excluir',
      columnWidth: 100,
      customRender: (row: any) => <DeleteBankDetailsCell row={row} personIdLogged={personIdLogged} />
    }
  ];

  const handleSupplierChange = (option: IOption | null) => {
    setSelectedSupplier(option);
  };

  useEffect(() => {
    runSuppliersGql();
  }, []);

  useEffect(() => {
    if (selectedSupplier) {
      resetSupplier({});
      resetSupplierData();
      runSupplierGql({
        variables: {
          personId: +(selectedSupplier?.key || 0)
        }
      });
      setValueSupplier('personId', +selectedSupplier?.key);
      setValueSupplier('personName', selectedSupplier?.value);
    }
  }, [selectedSupplier]);

  useEffect(() => {
    if (dataSupplier.length > 0) {
      runAssignorGql({
        variables: {
          idFDEm: dataSupplier[0]?.idFDEm
        }
      });

      runBankDetailsGql({
        variables: {
          idFDEm: dataSupplier[0]?.idFDEm
        }
      });

      setEditMode(true);

      setSupplierData((prevSupplierData) => {
        return {
          ...prevSupplierData,
          supplier: {
            ...dataSupplier[0],
            companyType: {
              key: dataSupplier[0].companyType,
              value: companyTypeOptions?.find((option) => option.key === +dataSupplier[0].companyType)?.value!
            },
            companySize: {
              key: dataSupplier[0].companySize,
              value: companySizeOptions?.find((option) => option.key === dataSupplier[0].companySize)?.value!
            },
            riskClassification: {
              key: dataSupplier[0].riskClassification,
              value: riskClassificationOptions?.find((option) => option.key === dataSupplier[0].riskClassification)
                ?.value!
            },
            receitaRegistrationStatus: {
              key: dataSupplier[0].receitaRegistrationStatus,
              value:
                receitaRegistrationStatusOptions?.find(
                  (option) => option.key === dataSupplier[0].receitaRegistrationStatus
                )?.value ?? ''
            }
          }
        };
      });
    }
  }, [loadingSupplier]);

  useEffect(() => {
    if (dataAssignor.length > 0 && dataBankDetails.length > 0) {
      setSupplierData((prevSupplierData) => {
        return {
          ...prevSupplierData,
          assignors: dataAssignor.map((assignor) => {
            return {
              operation: '',
              item: assignor
            };
          }),
          banksDetails: dataBankDetails.map((bankDetails) => {
            return {
              operation: '',
              item: {
                idFDBa: bankDetails.idFDBa,
                idFDEm: bankDetails.idFDEm,
                agency: bankDetails.agency,
                bankDataNotes: bankDetails.bankDataNotes,
                checkingAccount: bankDetails.checkingAccount,
                bank: {
                  key: bankDetails.bankInstitutionId.toString(),
                  value: bankDetails.bankInstitutionName
                }
              }
            };
          })
        };
      });
    }
  }, [loadingAssignor, loadingBankDetails]);

  useEffect(() => {
    if (supplierData.supplier.personId !== 0) {
      const formattedConstitutionDate = moment(supplierData.supplier.constitutionDate, 'DD/MM/YYYY').toDate();
      resetSupplier({
        ...supplierData.supplier,
        constitutionDate: formattedConstitutionDate
      });
    } else {
      setEditMode(false);
      resetSupplier({});
    }
  }, [supplierData.supplier]);

  useEffect(() => {
    const errors = [
      errorSuppliers,
      errorSupplier,
      errorAssignor,
      errorBankDetails,
      errorRegisterSupplier,
      errorUpdateSupplier
    ];

    const hasErrors = errors.some((errorItem) => errorItem && errorItem.graphQLErrors[0].extensions?.code !== '404');

    if (hasErrors) {
      if (DEBUG === 'TRUE') {
        const errorMsg = errors.find((error) => error);
        console.log(`Erro! Error:(message: ${errorMsg} )`);
      } else {
        toggleSnackbar({
          message: 'Ocorreu um problema ao executar a ação. Por favor, informe ao suporte.',
          type: 'error'
        });
      }
    }
  }, [errorSuppliers, errorSupplier, errorAssignor, errorBankDetails, errorRegisterSupplier, errorUpdateSupplier]);

  useEffect(() => {
    const setSupplier = async () => {
      if (!isVisaoTok) {
        const { id, nome } = await GetUserInfo();
        setSelectedSupplier({
          key: id,
          value: nome
        });
      }
    };
    setSupplier();
  }, []);

  return (
    <SupplierContext.Provider value={{ supplierData, setSupplierData, hasChanges, setHasChanges }}>
      <Snackbar />
      <PageBarHeader title="Cadastro Complementar" />
      {isVisaoTok && (
        <div className={classes.supplierSelector}>
          <Autocomplete
            showKeyProperty
            loading={loadingSuppliers}
            data={dataSuppliers || []}
            keyProperty="personId"
            valueProperty="supplierName"
            onChange={handleSupplierChange}
            value={selectedSupplier}
            label="Selecione o fornecedor"
          />
        </div>
      )}
      <div className={classes.container}>
        {isVisaoTok && !selectedSupplier && (
          <div className={classes.emptyListContainer}>
            <ListaVazia tipoMensagem="Edit" text="Selecione o fornecedor" textDescription="Para iniciar o cadastro" />
          </div>
        )}
        {(loadingSupplier || loadingRegisterSupplier || loadingUpdateSupplier) && (
          <div className={classes.loaderContainer}>
            <CircularProgress />
          </div>
        )}
        {selectedSupplier && !loadingSupplier && !loadingRegisterSupplier && !loadingUpdateSupplier && (
          <div className={classes.content}>
            <form>
              <Grid className={classes.formsContainer} container spacing={2}>
                <Grid item xs={12} sm={12} md={12}>
                  <Typography>INFORMAÇÕES GERAIS</Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={3} lg={2}>
                  <TextField
                    value={selectedSupplier?.key}
                    fullWidth
                    variant="outlined"
                    placeholder=""
                    label="Id do Fornecedor"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={9} lg={6}>
                  <TextField
                    value={selectedSupplier?.value}
                    fullWidth
                    variant="outlined"
                    placeholder=""
                    label="Nome do Fornecedor"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={4}>
                  <Controller
                    name="cnpj"
                    control={controlSupplier}
                    defaultValue=""
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <TextField
                        value={value}
                        onChange={onChange}
                        fullWidth
                        variant="outlined"
                        placeholder="CNPJ"
                        label={`CNPJ${editMode && dirtyFieldsSupplier['cnpj'] ? ' *' : ''}`}
                        error={!!error}
                        helperText={error?.message}
                        className={editMode && dirtyFieldsSupplier['cnpj'] ? classes.attentionHighlight : ''}
                        InputProps={{
                          inputComponent: CNPJTextMaskCustom as any
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                  <DatePicker
                    control={controlSupplier}
                    name="constitutionDate"
                    variant="outlined"
                    defaultValue={new Date()}
                    label={`Data da constituição${editMode && dirtyFieldsSupplier['constitutionDate'] ? ' *' : ''}`}
                    className={editMode && dirtyFieldsSupplier['constitutionDate'] ? classes.attentionHighlight : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={5}>
                  <Controller
                    name="companyType"
                    control={controlSupplier}
                    defaultValue=""
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <Autocomplete
                        data={companyTypeOptions}
                        keyProperty="key"
                        valueProperty="value"
                        onChange={onChange}
                        value={value}
                        label={`Tipo de sociedade${editMode && dirtyFieldsSupplier['companyType'] ? ' *' : ''}`}
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message}
                        className={editMode && dirtyFieldsSupplier['companyType'] ? classes.attentionHighlight : ''}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={4}>
                  <Controller
                    name="companySize"
                    control={controlSupplier}
                    defaultValue=""
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <div>
                        <Autocomplete
                          data={companySizeOptions}
                          keyProperty="key"
                          valueProperty="value"
                          onChange={onChange}
                          value={value}
                          label={`Porte${editMode && dirtyFieldsSupplier['companySize'] ? ' *' : ''}`}
                          variant="outlined"
                          error={!!error}
                          helperText={error?.message}
                          className={editMode && dirtyFieldsSupplier['companySize'] ? classes.attentionHighlight : ''}
                        />
                      </div>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Controller
                    name="annualBilling"
                    control={controlSupplier}
                    defaultValue=""
                    render={({ field: { ref, onChange, ...rest }, fieldState: { error } }) => (
                      <NumericFormat
                        thousandSeparator="."
                        decimalSeparator=","
                        prefix="R$ "
                        fixedDecimalScale
                        decimalScale={2}
                        getInputRef={ref}
                        customInput={TextField}
                        onValueChange={({ floatValue }) => {
                          onChange(floatValue);
                        }}
                        fullWidth
                        variant="outlined"
                        placeholder="Faturamento anual"
                        label={`Faturamento anual${editMode && dirtyFieldsSupplier['annualBilling'] ? ' *' : ''}`}
                        error={!!error}
                        helperText={error?.message}
                        {...rest}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Controller
                    name="creditLimit"
                    control={controlSupplier}
                    defaultValue=""
                    render={({ field: { value, onChange, ...rest }, fieldState: { error } }) => (
                      <TextField
                        value={value}
                        onChange={onChange}
                        fullWidth
                        variant="outlined"
                        placeholder="Limite crédito"
                        label={`Limite crédito${editMode && dirtyFieldsSupplier['creditLimit'] ? ' *' : ''}`}
                        error={!!error}
                        helperText={error?.message}
                        {...rest}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Controller
                    name="socialCapital"
                    control={controlSupplier}
                    defaultValue=""
                    render={({ field: { value, onChange, ...rest }, fieldState: { error } }) => (
                      <TextField
                        value={value}
                        onChange={onChange}
                        fullWidth
                        variant="outlined"
                        placeholder="Capital social"
                        label={`Capital social${editMode && dirtyFieldsSupplier['socialCapital'] ? ' *' : ''}`}
                        error={!!error}
                        helperText={error?.message}
                        {...rest}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={3}>
                  <Controller
                    name="minApprovalSignatures"
                    control={controlSupplier}
                    defaultValue=""
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <TextField
                        className={
                          editMode && dirtyFieldsSupplier['minApprovalSignatures'] ? classes.attentionHighlight : ''
                        }
                        value={value}
                        onChange={onChange}
                        type="number"
                        inputProps={{ min: 0 }}
                        fullWidth
                        variant="outlined"
                        placeholder="Nº Min. Assin. p/ Aprov."
                        label={`Nº Min. Assin. p/ Aprov.${
                          editMode && dirtyFieldsSupplier['minApprovalSignatures'] ? ' *' : ''
                        }`}
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={4}>
                  <Controller
                    name="riskClassification"
                    control={controlSupplier}
                    defaultValue=""
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <Autocomplete
                        className={
                          editMode && dirtyFieldsSupplier['riskClassification'] ? classes.attentionHighlight : ''
                        }
                        data={riskClassificationOptions}
                        keyProperty="key"
                        valueProperty="value"
                        onChange={onChange}
                        value={value}
                        label={`Classificação de Risco${
                          editMode && dirtyFieldsSupplier['riskClassification'] ? ' *' : ''
                        }`}
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={5} lg={5}>
                  <Controller
                    name="receitaRegistrationStatus"
                    control={controlSupplier}
                    defaultValue=""
                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                      <Autocomplete
                        className={
                          editMode && dirtyFieldsSupplier['receitaRegistrationStatus'] ? classes.attentionHighlight : ''
                        }
                        data={receitaRegistrationStatusOptions}
                        keyProperty="key"
                        valueProperty="value"
                        onChange={onChange}
                        value={value}
                        label={`Situação cadastral na Receita Federal${
                          editMode && dirtyFieldsSupplier['receitaRegistrationStatus'] ? ' *' : ''
                        }`}
                        variant="outlined"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </form>
            <Divider variant="middle" />
            <AssignorRegistration
              editMode={false}
              personIdLogged={personIdLogged}
              isAssinorRegistrationModalOpen={openAssignorModal}
              onClose={() => {
                setOpenAssignorModal(false);
              }}
            />
            <Grid item xs={12} sm={12} md={12}>
              <div className={classes.assignorSessionHeader}>
                <Typography>PESSOAS CEDENTES</Typography>
                <Button variant="text" color="primary" onClick={() => setOpenAssignorModal(true)}>
                  Adicionar Pessoa Cedente
                </Button>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              {!loadingAssignor && supplierData.assignors.length > 0 && (
                <GenericTable
                  columns={columns}
                  data={
                    supplierData.assignors.map((assignor) => {
                      return {
                        ...assignor.item,
                        highlight: editMode && hasChanges && assignor.operation !== '' ? true : false,
                        highlightColor:
                          assignor.operation === 'A' || assignor.operation === 'I'
                            ? '#FFF8DC'
                            : assignor.operation === 'E' // eslint-disable-next-line prettier/prettier
                              ? '#ffdbdb' // eslint-disable-next-line prettier/prettier
                              : '',
                        strike: assignor.operation === 'E'
                      };
                    }) || []
                  }
                />
              )}
              {loadingAssignor && (
                <div className={classes.loaderAssignor}>
                  <CircularProgress />
                </div>
              )}
              {(!supplierData.assignors || supplierData.assignors.length === 0) && !loadingAssignor && (
                <Typography style={{ textAlign: 'center' }}>Não há registros de pessoas cedentes.</Typography>
              )}
            </Grid>
            <BankDetailsRegistration
              isBankDetailsRegistrationModalOpen={openBankDetailsModal}
              personIdLogged={personIdLogged}
              onClose={() => setOpenBankDetailsModal(false)}
            />
            <div className={classes.sessionBankDetails}>
              <Grid item xs={12} sm={12} md={12}>
                <div className={classes.assignorSessionHeader}>
                  <Typography>DADOS BANCÁRIOS</Typography>
                  <Button variant="text" color="primary" onClick={() => setOpenBankDetailsModal(true)}>
                    Adicionar Dados Bancários
                  </Button>
                </div>
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                {supplierData.banksDetails.length > 0 && !loadingBankDetails && (
                  <GenericTable
                    columns={bankDetailsColumns}
                    data={
                      supplierData.banksDetails.map((bankDetails) => ({
                        ...bankDetails.item,
                        highlight: editMode && bankDetails.operation !== '' ? true : false,
                        highlightColor:
                          bankDetails.operation === 'A' || bankDetails.operation === 'I'
                            ? '#FFF8DC'
                            : bankDetails.operation === 'E' // eslint-disable-next-line prettier/prettier
                              ? '#ffdbdb' // eslint-disable-next-line prettier/prettier
                              : '',
                        strike: bankDetails.operation === 'E'
                      })) || []
                    }
                  />
                )}
                {loadingBankDetails && (
                  <div className={classes.loaderAssignor}>
                    <CircularProgress />
                  </div>
                )}
                {(!supplierData.banksDetails || supplierData.banksDetails.length === 0) && !loadingBankDetails && (
                  <Typography style={{ textAlign: 'center' }}>Não há registros de dados bancários.</Typography>
                )}
              </Grid>
            </div>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingBottom: '10px'
              }}
            >
              <Typography className={classes.captionUnsavedData}>
                {editMode && formStateSupplier.isDirty && '* - Dados não salvos, favor confirmar operação'}
              </Typography>
              <div className={classes.buttons}>
                {editMode && (
                  <Button
                    variant="text"
                    color="primary"
                    disabled={!hasChanges && isEmptyObject(dirtyFieldsSupplier)}
                    onClick={() => refreshSupplierData()}
                  >
                    CANCELAR
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmitSupplier(handleOnSubmit)}
                  disabled={editMode && !hasChanges && isEmptyObject(dirtyFieldsSupplier)}
                >
                  {editMode ? 'ATUALIZAR' : 'CADASTRAR'}
                </Button>
              </div>
            </Grid>
          </div>
        )}
      </div>
    </SupplierContext.Provider>
  );
};
